import { ReactNode } from "react";
import i18next from "i18next";

export const HTTP_CODES = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  NOT_MODIFIED: 304,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  GONE: 410,
  INTERNAL_SERVER_ERROR: 500,
} as const satisfies Record<string, number>;

export type ServerError = Record<string, unknown> & {
  location?: string;
  name?: string;
  description: string;
  error_code?: string;
};

export type ServerErrorResponse = {
  status?: "error";
  errors: ServerError[];
};

export class ApiRequestError extends Error {
  response: Response | undefined;
  responseBody: unknown;

  constructor(
    response: Response | undefined,
    responseBody: unknown,
    originalError?: Error
  ) {
    super();
    this.response = response;
    this.responseBody = responseBody;
    this.cause = originalError;
  }

  get errors(): ServerError[] | undefined {
    return isServerErrorResponse(this.responseBody)
      ? this.responseBody.errors
      : undefined;
  }

  get isUnauthorized() {
    return (
      this.response?.status === HTTP_CODES.UNAUTHORIZED ||
      this.response?.status === HTTP_CODES.FORBIDDEN
    );
  }

  get serverDescriptions(): string | undefined {
    const text = this.errors
      ?.map(e => e.description)
      .filter(Boolean)
      .join(" ");
    return text || undefined;
  }

  get errorCode(): string | undefined {
    return this.errors?.[0]?.error_code;
  }

  get message() {
    if (!navigator.onLine) {
      return ApiRequestError.OFFLINE_ERROR;
    }

    if (this.isUnauthorized) {
      // Only a labelled refusal is worded for the reader. An expired session and a
      // missing role carry no code, and their text is aimed at a developer.
      return (this.errorCode && this.serverDescriptions) || ApiRequestError.AUTH_ERROR;
    }

    if (this.response && this.response.status >= HTTP_CODES.INTERNAL_SERVER_ERROR) {
      return ApiRequestError.SERVER_ERROR;
    }

    if (isServerErrorResponse(this.responseBody)) {
      return this.serverDescriptions ?? "";
    }

    return this.cause instanceof Error
      ? this.cause.message
      : "An unknown error occurred.";
  }

  toString() {
    return this.message;
  }

  static SERVER_ERROR =
    "We were unable to perform your request, and our engineers were notified. In the meantime, please try again.";
  static OFFLINE_ERROR =
    "Oops, network issues appear to be interfering with your request. Please change networks or try again later";
  static AUTH_ERROR =
    "Your session has expired or you are not allowed to perform these actions. Please try to log in again.";
}

export type ErrorDetail = {
  text: string;
  error?: ServerError;
};

/**
 * Per-code overrides for `<ApiErrorBanner>`, keyed by `ServerError.error_code`.
 *
 * Nodes rather than translation keys, because a message can go past text: markup, a
 * link, or a control that makes a request of its own. The function form reads the
 * error's own fields, for a message that has to name what the server rejected.
 */
export type ErrorMessages<TCode extends string = string> = Partial<
  Record<TCode, ReactNode | ((error: ServerError) => ReactNode)>
>;

/**
 * Flattens whatever a fetcher rejected with into one detail per message.
 *
 * Each detail keeps the `ServerError` it came from: the code and the interpolation
 * fields are per-error, so three duplicate emails reject with three of each.
 */
export function toErrorDetails(error: unknown): ErrorDetail[] {
  const details = collectDetails(error).filter(detail => detail.text);
  return details.length > 0
    ? details
    : [{ text: i18next.t("Common.errors.somethingWrong") }];
}

/**
 * The same messages as one string, for the few places that need text rather than
 * nodes — an `Error` being rethrown, or a control whose error prop takes a string.
 * Prefer holding the error itself and rendering `<ApiErrorBanner>`.
 */
export function toErrorText(error: unknown): string {
  return toErrorDetails(error)
    .map(detail => detail.text)
    .join("; ");
}

function collectDetails(error: unknown): ErrorDetail[] {
  if (error instanceof QueryError) {
    return collectDetails(error.fetchError);
  }
  if (error instanceof ApiRequestError) {
    // A 5xx, an offline request and a non-JSON body all carry no `errors`, and
    // `message` is the wording each of those was written to show the reader.
    return error.errors?.length ? error.errors.map(toDetail) : [{ text: error.message }];
  }
  if (error instanceof Error) {
    return [{ text: error.message }];
  }
  if (typeof error === "string") {
    return [{ text: error }];
  }
  return [];
}

const toDetail = (error: ServerError): ErrorDetail => ({
  text: error.description,
  error,
});

/**
 * What a query rejects with: the fetcher's own error, wrapped so a boundary can
 * tell a query failure from any other render-time throw.
 *
 * It lives here rather than in `utils/queries` because `toErrorDetails` has to name
 * it, and importing the query barrel from here would cycle through the `services/*`
 * modules that build their factories while this one is still evaluating.
 */
export class QueryError extends Error {
  fetchError: unknown;

  constructor(error: unknown) {
    super(`Query function returned error: ${toErrorText(error)}`);
    this.fetchError = error;
  }

  get isAbortError() {
    return (
      this.fetchError instanceof DOMException && this.fetchError.name === "AbortError"
    );
  }

  /** The server had nothing at that address, as opposed to failing to answer. */
  get isNotFound() {
    return (
      this.fetchError instanceof ApiRequestError &&
      this.fetchError.response?.status === 404
    );
  }
}

export function isServerErrorResponse(body: unknown): body is ServerErrorResponse {
  return (
    !!body &&
    typeof body === "object" &&
    "errors" in body &&
    (body as { status?: unknown }).status === "error"
  );
}
